import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { site_url } from './../config.js';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { AuthContext } from './../AuthContext';
import Swal from 'sweetalert2';
import { FormatDate } from './../Function';

function DenominationDetailsView() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const denominationId = params.get('id');
  const w_name = params.get('w_name');
  const { user } = useContext(AuthContext);
  const [denomination, setDenomination] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDenomination = async () => {
      if (!denominationId) {
        Swal.fire({
          title: 'Error!',
          text: 'Denomination ID is missing',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate(-1);
        });
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${site_url}/edit_denomination/${denominationId}`);
        if (res.data && res.data.status === 'success' && res.data.denomination) {
          setDenomination(res.data.denomination);
        } else {
          throw new Error('Failed to fetch denomination data');
        }
      } catch (error) {
        console.error('Error fetching denomination:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || error.message || 'Failed to load denomination',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate(-1);
        });
      } finally {
        setLoading(false);
      }
    };

    if (user && user.id && denominationId) {
      fetchDenomination();
    }
  }, [denominationId, user, navigate]);

  if (loading) {
    return (
      <div className="container p-3">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading denomination details...</p>
        </div>
        <Sidebar />
      </div>
    );
  }

  if (!denomination) {
    return (
      <div className="container p-3">
        <div className="text-center p-5">
          <p>Denomination not found</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
        <Sidebar />
      </div>
    );
  }

  const routeTotal = parseFloat(denomination.totalcollectionamount || 0);
  const collectionTotal = parseFloat(denomination.totalrouteamount || 0);
  const balance = routeTotal - collectionTotal;

  return (
    <div className="container p-3">
      <div className="d-flex align-items-center justify-content-between mt-5 mb-4">
        <h3>Denomination Details</h3>
        <button 
          className="btn btn-outline-secondary" 
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Denomination Information</h4>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Worker Name:</strong>
              <p className="mb-2">{w_name || 'N/A'}</p>
            </div>
            <div className="col-md-6">
              <strong>Route:</strong>
              <p className="mb-2">{denomination.route_name || `Route ID: ${denomination.routeid}`}</p>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Collection Date:</strong>
              <p className="mb-2">{FormatDate(denomination.collectiondate) || denomination.collectiondate}</p>
            </div>
            <div className="col-md-6">
              <strong>Denomination ID:</strong>
              <p className="mb-2">{denomination.id}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3">
        <div className="card-header bg-info text-white">
          <h4 className="mb-0">Denomination Breakdown</h4>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{width:'40%'}}>Denomination</th>
                  <th style={{width:'30%'}} className="text-center">Count</th>
                  <th style={{width:'30%'}} className="text-center">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>₹ 500</b></td>
                  <td className="text-center">{denomination['500amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['500amount']) || 0) * 500).toLocaleString('en-IN')}</td>
                </tr>
                <tr>
                  <td><b>₹ 200</b></td>
                  <td className="text-center">{denomination['200amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['200amount']) || 0) * 200).toLocaleString('en-IN')}</td>
                </tr>
                <tr>
                  <td><b>₹ 100</b></td>
                  <td className="text-center">{denomination['100amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['100amount']) || 0) * 100).toLocaleString('en-IN')}</td>
                </tr>
                <tr>
                  <td><b>₹ 50</b></td>
                  <td className="text-center">{denomination['50amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['50amount']) || 0) * 50).toLocaleString('en-IN')}</td>
                </tr>
                <tr>
                  <td><b>₹ 20</b></td>
                  <td className="text-center">{denomination['20amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['20amount']) || 0) * 20).toLocaleString('en-IN')}</td>
                </tr>
                <tr>
                  <td><b>₹ 10</b></td>
                  <td className="text-center">{denomination['10amount'] || 0}</td>
                  <td className="text-center">₹ {((parseInt(denomination['10amount']) || 0) * 10).toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-4">
          <div className="card border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Route Total</span>
                <h4 className="text-primary mb-0">₹ {routeTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Collection Total</span>
                <h4 className="text-success mb-0">₹ {collectionTotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Balance</span>
                <h4 className={`mb-0 ${balance >= 0 ? 'text-warning' : 'text-danger'}`}>
                  ₹ {balance.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 mb-5">
        <button className="btn btn-primary" onClick={() => navigate(-1)}>
          Back to List
        </button>
      </div>

      <Footer />
      <Sidebar />
    </div>
  );
}

export default DenominationDetailsView;
